import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const SignalMeshFeesPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Signal Mesh Execution Fees & Reward Distribution - SELF Labs</title>
        <meta name="description" content="Learn about Harmony Signal Economy execution pathways and reward structures - Effective Date: April 1, 2025" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              Signal Mesh Execution Fees & Reward Distribution
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            {/* Introduction */}
            <section className="mb-16">
              <div className="prose prose-invert max-w-none">
                <p className="text-xl">Welcome to the Harmony Signal Economy, the sovereign execution layer embedded within the SELF Phone ecosystem.</p>
                <p>In Harmony, media is the operating system — every AI agent execution begins with a Harmony-enhanced piece of media: a song, a podcast, a livestream, a video, or ambient signal.</p>
                <p>Once a Harmony signal is detected, it triggers autonomous AI agent execution across the decentralized Signal Mesh Network. Depending on the task's complexity and location, Harmony optimizes execution through one of four primary pathways.</p>
                <p>To maintain network health, development, and user sovereignty, a flat 10% protocol fee is applied across all executions. The remaining 90% of the execution value is distributed among participants — device owners, content creators, and host locations — based on their role in the transaction.</p>
                <p>All transactions and rewards are permanently and transparently logged in the SELF Ledger.</p>
              </div>
            </section>

            {/* Signal Mesh Execution Pathways */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                📋 Signal Mesh Execution Pathways and Reward Structures
              </h2>

              {/* Local Agent Execution */}
              <div className="mb-12 bg-indigo-950/30 rounded-xl p-8 border border-indigo-500/20">
                <h3 className="text-2xl font-semibold mb-4 text-indigo-400">1. Local Agent Execution</h3>
                <p className="text-gray-400 italic mb-4">(SELF Phone or Flash Kit Node)</p>
                
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Execution Type:</h4>
                  <p className="text-gray-300">Agent is fully processed locally on the user's device without any external relays.</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Ideal For:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Offline environments</li>
                    <li>Personal automation</li>
                    <li>Private workflows</li>
                    <li>In-home task execution</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">How It Works:</h4>
                  <p className="text-gray-300">The SELF Phone or Flash Kit Node independently detects and executes the full Harmony workflow without needing mesh relays or cloud servers.</p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Reward Split:</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-gray-300">Device owner (SELF Phone or Flash Kit Node)</div>
                    <div className="text-indigo-400 font-semibold">70%</div>
                    <div className="text-gray-300">Content creator (signal originator)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                    <div className="text-gray-300">Hosting location (if applicable)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                    <div className="text-gray-300">SELF Labs (Protocol Fee)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                  </div>
                </div>
              </div>

              {/* Mesh Execution */}
              <div className="mb-12 bg-indigo-950/30 rounded-xl p-8 border border-indigo-500/20">
                <h3 className="text-2xl font-semibold mb-4 text-indigo-400">2. Mesh Execution</h3>
                <p className="text-gray-400 italic mb-4">(Peer-to-Peer Device Cooperation)</p>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Execution Type:</h4>
                  <p className="text-gray-300">Signal detected by one device and executed by another nearby device inside the Harmony Mesh.</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Ideal For:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Cafés, smart venues, concerts, cities</li>
                    <li>Group environments, decentralized community hubs</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">How It Works:</h4>
                  <p className="text-gray-300">Two devices collaborate:</p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1 mt-2">
                    <li>Device A (Listener) detects the Harmony signal embedded in media.</li>
                    <li>Device B (Executor) executes the AI agent task triggered by the signal.</li>
                    <li>Harmony Mesh coordinates secure validation between devices.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Reward Split:</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-gray-300">Device A (Listener/Detector)</div>
                    <div className="text-indigo-400 font-semibold">35%</div>
                    <div className="text-gray-300">Device B (Executor/Processor)</div>
                    <div className="text-indigo-400 font-semibold">35%</div>
                    <div className="text-gray-300">Content creator</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                    <div className="text-gray-300">Venue or location hosting the event</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                    <div className="text-gray-300">SELF Labs (Protocol Fee)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                  </div>
                </div>
              </div>

              {/* Q-Layer Execution */}
              <div className="mb-12 bg-indigo-950/30 rounded-xl p-8 border border-indigo-500/20">
                <h3 className="text-2xl font-semibold mb-4 text-indigo-400">3. Q-Layer / Cloud Execution</h3>
                <p className="text-gray-400 italic mb-4">(Heavy Task, Quantum Compute, or Enterprise APIs)</p>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Execution Type:</h4>
                  <p className="text-gray-300">Agent is routed to Harmony's backend infrastructure (Quantum clusters, secure routing servers).</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Ideal For:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Enterprise automations</li>
                    <li>Finance, legal, compliance workflows</li>
                    <li>Heavy data processing</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">How It Works:</h4>
                  <p className="text-gray-300">Tasks too large or complex for local or mesh execution are dynamically routed to the Harmony Q-Layer backend for completion.</p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Reward Split:</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-gray-300">User or device that initiated the agent trigger</div>
                    <div className="text-indigo-400 font-semibold">50%</div>
                    <div className="text-gray-300">Content creator (signal originator)</div>
                    <div className="text-indigo-400 font-semibold">20%</div>
                    <div className="text-gray-300">Host environment, service provider, or context</div>
                    <div className="text-indigo-400 font-semibold">20%</div>
                    <div className="text-gray-300">SELF Labs (Protocol Fee)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                  </div>
                </div>
              </div>

              {/* Creator-Earned Execution */}
              <div className="mb-12 bg-indigo-950/30 rounded-xl p-8 border border-indigo-500/20">
                <h3 className="text-2xl font-semibold mb-4 text-indigo-400">4. Creator-Earned Execution</h3>
                <p className="text-gray-400 italic mb-4">(Signal-Triggered Remote Agent Launch)</p>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Execution Type:</h4>
                  <p className="text-gray-300">Content creator embeds Harmony signals into media, allowing remote agent execution on end-user devices.</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Ideal For:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Musicians, streamers, influencers</li>
                    <li>Artists embedding automation in their content</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">How It Works:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Creator embeds a Harmony signal into a song, video, or livestream.</li>
                    <li>When a user's SELF Phone detects it, an agent is triggered remotely.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-indigo-300">Reward Split:</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-gray-300">Executing node or device</div>
                    <div className="text-indigo-400 font-semibold">50%</div>
                    <div className="text-gray-300">Media creator (signal originator)</div>
                    <div className="text-indigo-400 font-semibold">30%</div>
                    <div className="text-gray-300">Hosting platform (e.g., Twitch, YouTube, event venue)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                    <div className="text-gray-300">SELF Labs (Protocol Fee)</div>
                    <div className="text-indigo-400 font-semibold">10%</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Summary Table */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                📊 Summary of Signal Mesh Execution Rewards
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-indigo-500/20">
                      <th className="py-4 px-6 text-left text-indigo-400">Execution Type</th>
                      <th className="py-4 px-6 text-left text-indigo-400">Contributor</th>
                      <th className="py-4 px-6 text-left text-indigo-400">Reward Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-500/20">
                    {/* Local Execution */}
                    <tr>
                      <td className="py-4 px-6 text-gray-300" rowSpan={4}>Local Execution</td>
                      <td className="py-4 px-6 text-gray-300">Device Owner</td>
                      <td className="py-4 px-6 text-indigo-400">70%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Content Creator</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Host Location</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">SELF Labs (Protocol Fee)</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    {/* Mesh Execution */}
                    <tr>
                      <td className="py-4 px-6 text-gray-300" rowSpan={5}>Mesh Execution</td>
                      <td className="py-4 px-6 text-gray-300">Listener (Device A)</td>
                      <td className="py-4 px-6 text-indigo-400">35%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Executor (Device B)</td>
                      <td className="py-4 px-6 text-indigo-400">35%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Content Creator</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Host Location</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">SELF Labs (Protocol Fee)</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    {/* Q-Layer Execution */}
                    <tr>
                      <td className="py-4 px-6 text-gray-300" rowSpan={4}>Q-Layer Execution</td>
                      <td className="py-4 px-6 text-gray-300">Trigger Device/User</td>
                      <td className="py-4 px-6 text-indigo-400">50%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Content Creator</td>
                      <td className="py-4 px-6 text-indigo-400">20%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Host/Service Context</td>
                      <td className="py-4 px-6 text-indigo-400">20%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">SELF Labs (Protocol Fee)</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    {/* Creator-Earned Execution */}
                    <tr>
                      <td className="py-4 px-6 text-gray-300" rowSpan={4}>Creator-Earned Execution</td>
                      <td className="py-4 px-6 text-gray-300">Executing Device</td>
                      <td className="py-4 px-6 text-indigo-400">50%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Content Creator</td>
                      <td className="py-4 px-6 text-indigo-400">30%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">Host Platform</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-gray-300">SELF Labs (Protocol Fee)</td>
                      <td className="py-4 px-6 text-indigo-400">10%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Important Principles */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                📢 Important Principles
              </h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-indigo-400">10% Flat Protocol Fee:</h3>
                  <p className="text-gray-300">Every execution across the Signal Mesh contributes to sustaining, expanding, and protecting the ecosystem via SELF Labs infrastructure development.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-indigo-400">Transparent Ledger:</h3>
                  <p className="text-gray-300">All reward distributions are immutably logged on the SELF Ledger for public verification.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-indigo-400">Participation Sovereignty:</h3>
                  <p className="text-gray-300">Device owners control their participation. Signal Execution tasks are opt-in for devices when Harmony Mesh Participation is enabled.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-indigo-400">Global Scalability:</h3>
                  <p className="text-gray-300">Harmony's layered execution pathways allow devices of all capabilities — personal phones, mesh clusters, cloud servers — to contribute and earn.</p>
                </div>
              </div>
            </section>

            {/* Final Note */}
            <section className="mb-16 bg-indigo-950/30 rounded-xl p-8 border border-indigo-500/20">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">📜 Final Note</h2>
              <div className="prose prose-invert max-w-none">
                <p>Participation in the Harmony Signal Economy is voluntary.</p>
                <p>By participating, you agree to the <Link to="/mesh-license" className="text-indigo-400 hover:text-indigo-300">Harmony Mesh Participant License</Link> and all SELF Labs ecosystem <Link to="/terms" className="text-indigo-400 hover:text-indigo-300">Terms and Conditions</Link>.</p>
              </div>
            </section>
          </motion.div>

          {/* Footer */}
          <div className="mt-20 pt-12 border-t border-indigo-500/20">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">SELF Labs Inc.</h3>
                  <p className="text-gray-400">Powered by Role Model AI + Aura Protocol</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Legal</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Terms & Conditions
                    </Link>
                    <Link to="/privacy" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/reseller-agreement" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Reseller Agreement
                    </Link>
                    <Link to="/mesh-license" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Mesh License
                    </Link>
                    <Link to="/developer-agreement" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Developer Agreement
                    </Link>
                    <Link to="/signal-mesh-fees" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Signal Mesh Fees
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Contact</h3>
                  <div className="space-y-2">
                    <a href="mailto:data@rolemodel.ai" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      data@rolemodel.ai
                    </a>
                    <a href="https://x.com/selflabsinc" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      @selflabsinc
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-indigo-500/20 text-center text-sm text-gray-400">
                <p>&copy; {new Date().getFullYear()} SELF Labs Inc. All rights reserved.</p>
                <p className="mt-2">Protected by PureCipher's OMNISEAL™ encryption suite</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignalMeshFeesPage; 